import { PdfViewerSession } from '../../../../types/types';
import {
    MAX_BATCH_RENDER_PIXELS,
    PAGES_BATCH_MAX_SPAN,
    PREFETCH_BATCH_MIN,
    IMAGE_FORMAT,
    PREFETCH_OFFSETS,
    TILE_SIZE,
    TILES_BATCH_MIN,
    TILE_EPSILON,
    RATE_LIMIT_MAX_RETRIES,
} from './constants';
import {
    PdfViewerError, isAbortError, isRateLimited, isUnauthorized,
} from './errors';
import { parseMultipart, parseTileLabel } from './multipart';
import { OverviewCache } from './overviewCache';
import RateGate, { retryDelayMs } from './rateGate';
import RequestQueue from './requestQueue';
import {
    OutlineItem, PageInfo, SearchPage, TilePart,
} from '../types';
import { decodeBlob, releaseBitmap } from '../helpers/bitmap';
import { quantizeOverviewWidth } from '../helpers/zoom';
import isAllowedServerUrl from '../helpers/serverOrigin';
import isExpiringWithin from '../helpers/jwtExpiry';

export interface PdfViewerClientOptions {
    fetchFn?: typeof fetch;
    dpr?: number;
    apiUrl?: string;
    allowLoopbackHttp?: boolean;
    refreshSession?: () => Promise<PdfViewerSession>;
    onUnauthorized?: () => void;
    decode?: (blob: Blob) => Promise<ImageBitmap>;
    release?: (bitmap: ImageBitmap) => void;
}

export interface LoadTilesParams {
    page: number;
    pageSize: PageInfo;
    renderZoom: number;
    signal: AbortSignal;
    onTile: (tile: TilePart) => void;
}

type Params = Record<string, string | number>;

const HTTP_UNAUTHORIZED = 401;
const HTTP_TOO_MANY_REQUESTS = 429;
const TOKEN_REFRESH_MARGIN_MS = 60_000;

const noop = (): void => undefined;

export const tileGrid = (
    pageSize: PageInfo,
    renderZoom: number,
    tileSize: number,
): { columns: number; rows: number } => ({
    columns: Math.ceil((pageSize.w * renderZoom) / tileSize - TILE_EPSILON),
    rows: Math.ceil((pageSize.h * renderZoom) / tileSize - TILE_EPSILON),
});

export class PdfViewerClient {
    readonly queue = new RequestQueue();

    readonly dpr: number;

    readonly tileSize = TILE_SIZE;

    private session: PdfViewerSession;

    private readonly fetchFn: typeof fetch;

    private readonly apiUrl: string;

    private readonly allowLoopbackHttp: boolean;

    private readonly refreshSession?: () => Promise<PdfViewerSession>;

    private refreshing: Promise<void> | null = null;

    private refreshFailed = false;

    private readonly onUnauthorized?: () => void;

    private readonly decode: (blob: Blob) => Promise<ImageBitmap>;

    readonly release: (bitmap: ImageBitmap) => void;

    private readonly overviews: OverviewCache;

    private generation = 0;

    private cacheGeneration = 0;

    private readonly inflight = new Set<AbortController>();

    private readonly rateGate = new RateGate();

    private tilesBatch: boolean | null = null;

    private pagesBatch: boolean | null = null;

    constructor(session: PdfViewerSession, options: PdfViewerClientOptions = {}) {
        this.session = session;
        this.fetchFn = options.fetchFn ?? ((input, init) => fetch(input, init));
        this.dpr = options.dpr ?? 1;
        this.apiUrl = options.apiUrl ?? '';
        this.allowLoopbackHttp = !!options.allowLoopbackHttp;
        this.refreshSession = options.refreshSession;
        this.onUnauthorized = options.onUnauthorized;
        this.decode = options.decode ?? decodeBlob;
        this.release = options.release ?? releaseBitmap;
        this.overviews = new OverviewCache(this.release);
    }

    get tileCssSize(): number {
        return this.tileSize / this.dpr;
    }

    setSession(session: PdfViewerSession): void {
        if (session.jwt !== this.session.jwt) {
            this.refreshFailed = false;
        }
        this.session = session;
    }

    invalidate(): void {
        this.generation += 1;
    }

    dispose(): void {
        this.generation += 1;
        this.cacheGeneration += 1;
        this.inflight.forEach((controller) => controller.abort());
        this.inflight.clear();
        this.rateGate.cancel();
        this.queue.clear();
        this.overviews.clear();
    }

    getInfo(signal?: AbortSignal): Promise<PageInfo[]> {
        return this.request('info', {}, signal).then((response) => response.json());
    }

    getOutline(signal?: AbortSignal): Promise<OutlineItem[]> {
        return this.request('outline', {}, signal).then((response) => response.json());
    }

    search(query: string, start: number, count: number, limit: number, signal?: AbortSignal): Promise<SearchPage> {
        return this.request('search', {
            q: query, start, count, limit,
        }, signal).then((response) => response.json());
    }

    decodeBlob(blob: Blob): Promise<ImageBitmap> {
        return this.decode(blob);
    }

    async fetchThumbnail(page: number, width: number, signal: AbortSignal): Promise<ImageBitmap> {
        const blob = await this.fetchImage('page', { page, w: Math.round(width * this.dpr) }, false, signal);
        return this.decode(blob);
    }

    peekOverview(page: number, width: number): ImageBitmap | undefined {
        return this.overviews.get(PdfViewerClient.overviewKey(page, width))?.bitmap;
    }

    overview(page: number, width: number, prefetch = false): Promise<ImageBitmap | undefined> {
        const { cacheGeneration } = this;
        const key = PdfViewerClient.overviewKey(page, width);
        const hit = this.overviews.get(key);
        if (hit) {
            return hit.bitmap
                ? Promise.resolve(hit.bitmap)
                : hit.promise.then((bitmap) => (cacheGeneration === this.cacheGeneration
                    ? bitmap ?? this.overview(page, width, prefetch)
                    : undefined));
        }
        const promise = this.fetchImage('page', { page, w: width }, prefetch)
            .then((blob) => this.storeOverview(key, blob, cacheGeneration))
            .catch((error) => {
                if (cacheGeneration === this.cacheGeneration) {
                    this.overviews.delete(key);
                }
                throw error;
            });
        this.overviews.setPending(key, promise);
        return promise;
    }

    prefetchAround(current: number, pages: PageInfo[], zoom: number): void {
        const { generation } = this;
        const widthOf = (index: number): number => quantizeOverviewWidth(pages[index].w * zoom);
        const wanted = PREFETCH_OFFSETS
            .map((offset) => current + offset)
            .filter((index) => pages[index] && !this.overviews.has(PdfViewerClient.overviewKey(index, widthOf(index))));
        if (!wanted.length) {
            return;
        }
        if (this.pagesBatch !== false && wanted.length >= PREFETCH_BATCH_MIN) {
            this.prefetchBatch(wanted, generation, widthOf);
            return;
        }
        wanted.forEach((index) => this.overview(index, widthOf(index), true).catch(noop));
    }

    async loadPageTiles({
        page, pageSize, renderZoom, signal, onTile,
    }: LoadTilesParams): Promise<void> {
        const { columns, rows } = tileGrid(pageSize, renderZoom, this.tileSize);
        const single = async (): Promise<void> => {
            const jobs: Promise<void>[] = [];
            for (let y = 0; y < rows; y += 1) {
                for (let x = 0; x < columns; x += 1) {
                    jobs.push(this.fetchImage('tile', {
                        page, zoom: renderZoom, x, y, size: this.tileSize,
                    }, false, signal).then((blob) => {
                        if (!signal.aborted) {
                            onTile({ x, y, blob });
                        }
                    }));
                }
            }
            await Promise.all(jobs);
        };
        const exceedsBatchLimit = pageSize.w * pageSize.h * renderZoom * renderZoom > MAX_BATCH_RENDER_PIXELS;
        if (this.tilesBatch === false || columns * rows < TILES_BATCH_MIN || exceedsBatchLimit) {
            await single();
            return;
        }
        try {
            await this.queue.enqueue(async () => {
                const response = await this.request('tiles', {
                    page, zoom: renderZoom, size: this.tileSize, fmt: IMAGE_FORMAT,
                }, signal);
                const parts = await parseMultipart(response);
                if (!signal.aborted) {
                    parts.forEach(({ label, blob }) => onTile({ ...parseTileLabel(label), blob }));
                }
            }, false, signal);
            this.tilesBatch = true;
        } catch (error) {
            if (isAbortError(error) || signal.aborted || isUnauthorized(error) || isRateLimited(error)) {
                throw error;
            }
            if (this.tilesBatch === null) {
                this.tilesBatch = false;
            }
            await single();
        }
    }

    private static overviewKey(page: number, width: number): string {
        return `${page}|${width}`;
    }

    private prefetchBatch(wanted: number[], generation: number, widthOf: (index: number) => number): void {
        const { cacheGeneration } = this;
        const first = Math.min(...wanted);
        const last = Math.min(Math.max(...wanted), first + PAGES_BATCH_MAX_SPAN);
        const keyOf = (index: number): string => PdfViewerClient.overviewKey(index, widthOf(index));
        const pending: number[] = [];
        for (let index = first; index <= last; index += 1) {
            if (!this.overviews.has(keyOf(index))) {
                pending.push(index);
            }
        }
        let resolveGroup: (bitmaps: Map<number, ImageBitmap>) => void = noop;
        const group = new Promise<Map<number, ImageBitmap>>((resolve) => {
            resolveGroup = resolve;
        });
        pending.forEach((index) => {
            this.overviews.setPending(keyOf(index), group.then((bitmaps) => bitmaps.get(index)));
        });
        const settle = (bitmaps: Map<number, ImageBitmap>): void => {
            if (cacheGeneration !== this.cacheGeneration) {
                resolveGroup(new Map());
                return;
            }
            pending.forEach((index) => {
                if (!bitmaps.has(index)) {
                    this.overviews.delete(keyOf(index));
                }
            });
            resolveGroup(bitmaps);
        };
        this.queue.enqueue(async () => {
            const bitmaps = new Map<number, ImageBitmap>();
            if (generation !== this.generation) {
                settle(bitmaps);
                return;
            }
            try {
                const response = await this.request('pages', {
                    from: first, to: last, w: widthOf(first), fmt: IMAGE_FORMAT,
                });
                const parts = await parseMultipart(response);
                await Promise.all(parts.map(async ({ label, blob }) => {
                    const index = Number(label);
                    if (pending.includes(index)) {
                        const bitmap = await this.storeOverview(keyOf(index), blob, cacheGeneration);
                        if (bitmap) {
                            bitmaps.set(index, bitmap);
                        }
                    }
                }));
                this.pagesBatch = true;
                settle(bitmaps);
            } catch (error) {
                const keepsBatch = isRateLimited(error) || isUnauthorized(error);
                if (this.pagesBatch === null && !keepsBatch) {
                    this.pagesBatch = false;
                }
                settle(bitmaps);
                if (generation === this.generation && !keepsBatch) {
                    wanted.forEach((index) => this.overview(index, widthOf(index), true).catch(noop));
                }
            }
        }, true).catch(() => settle(new Map()));
    }

    private async storeOverview(key: string, blob: Blob, cacheGeneration: number): Promise<ImageBitmap | undefined> {
        if (cacheGeneration !== this.cacheGeneration) {
            return undefined;
        }
        const bitmap = await this.decode(blob);
        if (cacheGeneration !== this.cacheGeneration) {
            this.release(bitmap);
            return undefined;
        }
        this.overviews.setBitmap(key, bitmap);
        return bitmap;
    }

    private fetchImage(route: string, params: Params, prefetch: boolean, signal?: AbortSignal): Promise<Blob> {
        return this.queue.enqueue(
            async () => (await this.request(route, { ...params, fmt: IMAGE_FORMAT }, signal)).blob(),
            prefetch,
            signal,
        );
    }

    private refresh(): Promise<void> {
        const requestSession = this.refreshSession;
        if (!this.refreshing && requestSession) {
            this.refreshing = requestSession()
                .then((session) => {
                    if (session.documentId !== this.session.documentId) {
                        throw new PdfViewerError(0);
                    }
                    this.session = session;
                })
                .catch((error) => {
                    this.refreshFailed = true;
                    throw error;
                })
                .finally(() => {
                    this.refreshing = null;
                });
        }
        return this.refreshing ?? Promise.resolve();
    }

    private async refreshOrExpire(): Promise<void> {
        try {
            await this.refresh();
        } catch {
            this.onUnauthorized?.();
            throw new PdfViewerError(HTTP_UNAUTHORIZED);
        }
    }

    private canRefresh(): boolean {
        return !!this.refreshSession && !this.refreshFailed;
    }

    private async request(route: string, params: Params, signal?: AbortSignal): Promise<Response> {
        if (this.canRefresh() && (this.refreshing || isExpiringWithin(this.session.jwt, TOKEN_REFRESH_MARGIN_MS))) {
            await this.refreshOrExpire();
        }
        const usedJwt = this.session.jwt;
        let response = await this.send(route, params, signal);
        if (response.status === HTTP_UNAUTHORIZED && this.canRefresh()) {
            if (this.session.jwt === usedJwt || this.refreshing) {
                await this.refreshOrExpire();
            }
            response = await this.send(route, params, signal);
        }
        if (!response.ok) {
            if (response.status === HTTP_UNAUTHORIZED) {
                this.onUnauthorized?.();
            }
            throw new PdfViewerError(response.status);
        }
        return response;
    }

    private async send(route: string, params: Params, signal?: AbortSignal, attempt = 0): Promise<Response> {
        await this.rateGate.wait(signal);
        const response = await this.sendOnce(route, params, signal);
        if (response.status !== HTTP_TOO_MANY_REQUESTS || attempt >= RATE_LIMIT_MAX_RETRIES) {
            return response;
        }
        this.rateGate.pause(retryDelayMs(response.headers.get('Retry-After'), attempt));
        return this.send(route, params, signal, attempt + 1);
    }

    private async sendOnce(route: string, params: Params, signal?: AbortSignal): Promise<Response> {
        if (!isAllowedServerUrl(this.session.serverUrl, this.apiUrl, this.allowLoopbackHttp)) {
            throw new PdfViewerError(0);
        }
        const base = this.session.serverUrl.endsWith('/') ? this.session.serverUrl : `${this.session.serverUrl}/`;
        const query = new URLSearchParams(Object.entries(params).map(([key, value]) => [key, String(value)]));
        const queryString = query.toString();
        const document = encodeURIComponent(this.session.documentId);
        const url = `${base}docs/${document}/${route}${queryString ? `?${queryString}` : ''}`;
        const controller = new AbortController();
        const abort = (): void => controller.abort();
        if (signal?.aborted) {
            abort();
        }
        signal?.addEventListener('abort', abort, { once: true });
        this.inflight.add(controller);
        let response: Response;
        try {
            response = await this.fetchFn(url, {
                headers: { Authorization: `Bearer ${this.session.jwt}` },
                cache: 'no-store',
                signal: controller.signal,
            });
        } finally {
            this.inflight.delete(controller);
            signal?.removeEventListener('abort', abort);
        }
        return response;
    }
}
